import { useAuthStore } from '@/entities/auth';

import {
  deletePushNotificationSubscription,
  getPushNotificationCapability,
  testPushNotificationSubscription,
  upsertPushNotificationSubscription,
} from '../api/push-notifications-api';
import { createBrowserPushAdapter } from './browser-push-adapter';
import { createPushNotificationController, type PushNotificationController } from './push-notification-controller';
import {
  createPushSubscriptionOwnershipManager,
  PUSH_SUBSCRIPTION_OWNERSHIP_STORAGE_KEY,
} from './push-subscription-ownership';

let controller: PushNotificationController | null = null;

const getController = (): PushNotificationController => {
  const authStore = useAuthStore();

  controller ??= createPushNotificationController(
    createBrowserPushAdapter(),
    {
      getCapability: getPushNotificationCapability,
      upsert: upsertPushNotificationSubscription,
      delete: deletePushNotificationSubscription,
      test: testPushNotificationSubscription,
    },
    createPushSubscriptionOwnershipManager(localStorage),
    {
      isImpersonating: () => authStore.isImpersonating,
    },
  );

  return controller;
};

export const usePushNotifications = (): PushNotificationController => getController();

let lifecycleListenersRegistered = false;

export const registerPushNotificationLocalStateListeners = (): (() => void) => {
  if (lifecycleListenersRegistered) {
    return () => undefined;
  }

  lifecycleListenersRegistered = true;
  const refresh = (): void => {
    void getController().refreshLocalState();
  };
  const handleStorage = (event: StorageEvent): void => {
    if (event.key === PUSH_SUBSCRIPTION_OWNERSHIP_STORAGE_KEY) {
      refresh();
    }
  };
  const handleVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      refresh();
    }
  };

  window.addEventListener('storage', handleStorage);
  document.addEventListener('visibilitychange', handleVisibility);

  return () => {
    lifecycleListenersRegistered = false;
    window.removeEventListener('storage', handleStorage);
    document.removeEventListener('visibilitychange', handleVisibility);
  };
};
